import Color from 'color';
import { action, makeAutoObservable, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { logWithState } from '../../common/functions/console.function';
import { setCSS } from '../../common/functions/css.function';
import { useStoreUIV1 } from '../hooks/useStore.hook';
import { RootStoreMobX } from './root.store';

export type ThemeStoreContextType = {
  theme: 'dark' | 'light';
  defaultTheme: ThemeStoreContextType['theme'];
  color: {
    dark: string;
    light: string;
  };
};

const colorsMapper = {
  light: {
    background: '#F5F5F5',
    layout: '#ffffff',
    card: '#ffffff',
    border: '#dcdcdc',
    elevate: 'rgba(10, 10, 10, 0.5)',
    intermediate: '#dddddd',
    disabled: '#a0a0a0',
    label: '#848484',
    text: '#333333',
    yin: '#ffffff',
    yang: '#333333',
    shadow: 'rgba(0, 0, 0, 0.15)',
    error: '#ff4d4f',
    warn: '#f48c06',
    info: '#009fb7',
    yell: '#cfba00',
    success: '#51cf84',
    epic: '#ff36ab',
  },
  dark: {
    background: '#09090B',
    layout: '#18181B',
    card: '#09090B',
    border: '#4d4d4d',
    intermediate: '#383838',
    elevate: 'rgba(10, 10, 10, 0.5)',
    disabled: '#989898',
    label: '#DEDEDE',
    text: '#e8e8e8',
    yin: '#333333',
    yang: '#e8e8e8',
    shadow: 'rgba(0, 0, 0, 0.2)',
    error: '#ff4d4f',
    warn: '#c46b03',
    info: '#00798f',
    yell: '#cfba00',
    success: '#3a9e5c',
    epic: '#ff36ab',
  },
};

const getStoredTheme = (): ThemeStoreContextType['theme'] | null => {
  try {
    const stored = localStorage.getItem('theme');
    return stored === 'dark' || stored === 'light' ? stored : null;
  } catch {
    return null;
  }
};

const colors = Object.freeze({
  theme: {
    light: {
      ...colorsMapper.light,
    },
    dark: {
      ...colorsMapper.dark,
    },
  },
  base: {
    white: '#ffffff',
    black: '#333333',
  },
} as const);

export class ThemeStoreMobX {
  rootStore: RootStoreMobX;
  @observable public theme: ThemeStoreContextType['theme'] =
    getStoredTheme() ?? 'light';
  @observable public registryColors = colors;
  @observable private colorsTheme = colors.theme[this.theme];
  @observable private colorsDynamic?: ThemeStoreContextType['color'];
  @observable private colorsBase = colors.base;
  @observable public load: boolean = false;

  constructor(rootStore: RootStoreMobX) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  @action public init({
    color,
    defaultTheme,
  }: Pick<ThemeStoreContextType, 'color' | 'defaultTheme'>) {
    this.load = false;
    this.theme = getStoredTheme() ?? defaultTheme;
    this.colorsDynamic = color;
    this.setTheme(this.theme);
    this.load = true;
  }

  @action public getTheme() {
    return getStoredTheme();
  }

  @action public setTheme(defaultTheme: ThemeStoreContextType['theme']) {
    this.theme = defaultTheme;
    this.colorsTheme = colors.theme[this.theme];
    localStorage.setItem('theme', this.theme);
    this.setColorsTheme(this.theme);
    this.setColorsDynamic(this.theme);
    this.setColorsBase(this.theme);
    this.setColorsChart(this.theme);
  }

  @action private setColorsTheme(theme: ThemeStoreContextType['theme']) {
    Object.entries(this.colorsTheme).forEach(([key, value]) =>
      setCSS(`--color-${key}`, value as string),
    );

    const { error, warn, info, yell, success } = this.colorsTheme;
    Object.entries({ error, warn, info, yell, success }).forEach(
      ([key, value]) => {
        for (let i = 10; i <= 100; i += 10) {
          setCSS(
            `--color-${key}-alpha-${i}`,
            `${new Color(value).alpha(i / 100)}`,
          );
        }
      },
    );
  }

  @action private setColorsChart(theme: ThemeStoreContextType['theme']) {
    // Chart.defaults.borderColor = this.colorsTheme.border;
  }

  @action private setColorsDynamic(theme: ThemeStoreContextType['theme']) {
    if (this.colorsDynamic) {
      const colorPrimaryOver = !new Color(this.colorsDynamic[theme]).isDark()
        ? new Color(this.colorsBase.black).alpha(1).string()
        : new Color(this.colorsBase.white).alpha(1).string();

      setCSS(`--color-primary`, this.colorsDynamic[theme]);
      setCSS(`--color-primary-over`, colorPrimaryOver);

      for (let i = 5; i <= 100; i += 5) {
        setCSS(
          `--color-primary-alpha-${i}`,
          `${new Color(this.colorsDynamic[theme]).alpha(i / 100)}`,
        );
      }

      setCSS(
        '--color-selected',
        `${new Color(this.colorsDynamic[theme]).alpha(0.2)}`,
      );

      setCSS(
        '--color-over',
        `${new Color(this.colorsDynamic[theme]).alpha(0.1)}`,
      );
    }
  }

  @action private setColorsBase(theme: ThemeStoreContextType['theme']) {
    const colorInfoOver = new Color(this.colorsTheme.info).isDark()
      ? new Color(colors.base.white).alpha(1).toString()
      : new Color(colors.base.black).alpha(1).toString();

    setCSS(`--color-white`, colors.base.white);
    setCSS(`--color-black`, colors.base.black);
    setCSS(`--color-info-over`, colorInfoOver);
  }
}

export const ConfigTheme = observer(
  ({
    children,
    color,
    defaultTheme,
  }: { children: React.ReactNode } & Pick<
    ThemeStoreContextType,
    'color' | 'defaultTheme'
  >) => {
    const { ThemeStore } = useStoreUIV1();

    useEffect(() => {
      if (!ThemeStore.load) {
        logWithState({ state: 'INFO', value: 'ConfigTheme init' });
        ThemeStore.init({ color, defaultTheme });
      }

      if (ThemeStore.load) {
        logWithState({ state: 'INFO', value: 'ConfigTheme load' });
      }
    }, [ThemeStore.load]);

    return ThemeStore.load ? children : null;
  },
);
